import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, XIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { LanguageSwitcher } from './ui/LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { spring } from '../utils/motion';
interface WizardLayoutProps {
  step: number;
  total: number;
  direction: number;
  title: string;
  subtitle?: string;
  onBack: () => void;
  onNext: () => void;
  nextLabel?: string;
  canNext?: boolean;
  onSkip?: () => void;
  children: React.ReactNode;
}
export function WizardLayout({
  step,
  total,
  direction,
  title,
  subtitle,
  onBack,
  onNext,
  nextLabel,
  canNext = true,
  onSkip,
  children
}: WizardLayoutProps) {
  const {
    t
  } = useLanguage();
  return <div className="flex min-h-[100dvh] w-full flex-col bg-bg text-ink" data-id="element-850" data-mp="2iagi03ll5mgx">
      <header className="sticky top-0 z-10 bg-bg/90 backdrop-blur-md" data-id="element-851" data-mp="2icj6ukb059k2">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-5 py-4" data-id="element-852" data-mp="2n887ys2o6yih">
          <button type="button" onClick={onBack} aria-label={step === 0 ? 'Exit' : 'Back'} className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-ink-2 transition-colors duration-150 hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand" data-id="element-853" data-mp="f269n0qf882l">
            {step === 0 ? <XIcon className="h-5 w-5" data-id="element-854" data-mp="3s86dlc3j2y0b" /> : <ArrowLeftIcon className="h-5 w-5" data-id="element-855" data-mp="3s97ys9e9d0vr" />}
          </button>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-2" role="progressbar" aria-valuemin={1} aria-valuemax={total} aria-valuenow={step + 1} aria-label="Progress" data-id="element-856" data-mp="3nh6dxz30y1u4">
            <motion.div className="h-full w-full origin-left rounded-full bg-brand" initial={false} animate={{
            scaleX: (step + 1) / total
          }} transition={spring.smooth} data-id="element-857" data-mp="3dhp91jvljrz7" />
          </div>
          {onSkip && <button type="button" onClick={onSkip} className="shrink-0 rounded-full px-3 py-2 text-sm font-bold text-ink-2 transition-colors duration-150 hover:bg-surface-2 hover:text-ink" data-id="element-858" data-mp="2twfahlxtx15y">
              {t('common.skip')}
            </button>}
          <LanguageSwitcher id="wizard" data-id="element-859" data-mp="2pvn18soyiw9o" />
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-5 pb-36 pt-4" data-id="element-860" data-mp="2pxzpfnlb62v6">
        <motion.div key={step} initial={{
        x: direction * 32,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} transition={spring.smooth} data-id="element-861" data-mp="3dmizo8nx4ppw">
          <h1 className="text-[32px] font-bold leading-[1.08] tracking-[-0.015em] lg:text-[40px]" data-id="element-862" data-mp="33v4rlchwihl5">{title}</h1>
          {subtitle && <p className="mt-2 max-w-[52ch] text-[17px] leading-relaxed text-ink-2" data-id="element-863" data-mp="38swm8e9xkht1">{subtitle}</p>}
          <div className="mt-8" data-id="element-864" data-mp="d4ifu0m065y">{children}</div>
        </motion.div>
      </main>

      <footer className="fixed inset-x-0 bottom-0 z-10 border-t border-line bg-bg/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md" data-id="element-865" data-mp="57h50cxdtxlx">
        <div className="mx-auto flex max-w-2xl justify-end px-5 py-4" data-id="element-866" data-mp="aj4ekdzqvybj">
          <Button variant="accent" size="lg" className="w-full sm:w-auto sm:min-w-[220px]" disabled={!canNext} onClick={onNext} data-id="element-867" data-mp="aiw22n7jav0v">
            {nextLabel ?? t('common.continue')}
          </Button>
        </div>
      </footer>
    </div>;
}